import { supabase } from "@/integrations/supabase/client";
import { createPlaceholderImage } from "@/utils/imageFormatSupport";
import { getTransformationUrl } from "@/utils/imageTransformationUtils";

// Determine the correct bucket to use
const getServiceImageBucket = async (): Promise<string> => {
  try {
    // First check for the 'services' bucket (preferred now that we're removing service-photos)
    const { data: buckets, error } = await supabase.storage.listBuckets();
    if (error) throw error;
    
    if (buckets.some(bucket => bucket.name === 'services')) {
      return 'services';
    } else if (buckets.some(bucket => bucket.name === 'service-photos')) {
      console.warn('Using service-photos bucket, which is deprecated. Consider migrating to services bucket.');
      return 'service-photos';
    } else {
      console.warn('Creating services bucket since neither services nor service-photos bucket was found.');
      // Create the services bucket if it doesn't exist
      const { error: createError } = await supabase.storage.createBucket('services', {
        public: true,
        fileSizeLimit: 10 * 1024 * 1024 // 10MB max for service images
      });
      
      if (createError) {
        console.error('Error creating services bucket:', createError);
        return 'services'; // Return services anyway as the default
      }
      
      return 'services';
    }
  } catch (error) {
    console.error('Error checking available buckets:', error);
    return 'services'; // Default to 'services' if we can't check
  }
};

// New function to create placeholder for an image
export const createPlaceholderForImage = async (originalPath: string, bucket: string = 'services') => {
  if (!originalPath) {
    console.error('Cannot create placeholder: Missing original path');
    return null;
  }
  
  try {
    // Get the public URL of the original image
    const { data } = supabase.storage.from(bucket).getPublicUrl(originalPath);
    if (!data?.publicUrl) {
      console.error('Cannot create placeholder: Failed to get public URL');
      return null;
    }
    
    // Extract the filename
    const pathParts = originalPath.split('/');
    const filename = pathParts[pathParts.length - 1];
    
    // Check if bucket exists
    const { data: buckets } = await supabase.storage.listBuckets();
    if (!buckets?.some(b => b.name === 'placeholders')) {
      // Create the placeholders bucket if it doesn't exist
      const { error } = await supabase.storage.createBucket('placeholders', {
        public: true,
        allowedMimeTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/avif'],
        fileSizeLimit: 1024 * 1024 // 1MB limit for placeholders
      });
      
      if (error) {
        console.error('Failed to create placeholders bucket:', error);
      }
    }
    
    // Create a tiny placeholder image using client utility
    return await createPlaceholderImage(data.publicUrl, originalPath);
  } catch (error) {
    console.error('Error in createPlaceholderForImage:', error);
    return null;
  }
};

export const updateServiceImage = async (serviceId: number, file: File, isIndexImage: boolean = true) => {
  try {
    // Get the correct bucket to use
    const bucket = await getServiceImageBucket();
    
    // Enhanced filename generation with crop indicator
    const fileExt = file.name.split('.').pop()?.toLowerCase() || 'png';
    const timestamp = new Date().getTime();
    const uuid = crypto.randomUUID();
    const isCropped = file.name.includes('cropped') || file.type === 'image/jpeg';
    const prefix = isCropped ? 'cropped' : 'original';
    const sanitizedFileName = `${prefix}_${uuid}_${timestamp}.${fileExt}`;
    
    console.log(`Uploading ${isCropped ? 'cropped' : 'original'} file to ${bucket} bucket:`, sanitizedFileName, 'Content-Type:', file.type);
    
    // Upload the file to the services bucket
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(sanitizedFileName, file, {
        contentType: file.type,
        cacheControl: '3600',
        upsert: true
      });

    if (uploadError) {
      console.error('Upload error:', uploadError);
      throw uploadError;
    }

    console.log('File uploaded successfully, path:', uploadData?.path);

    // Enhanced checksum with crop information
    const checksum = `${file.size}-${file.lastModified}-${uuid}-${isCropped ? 'cropped' : 'original'}`;

    // Create placeholder for the image (automatically happens in client when needed)
    // We don't await this as we don't want to block the function
    window.setTimeout(() => {
      try {
        if (uploadData?.path) {
          createPlaceholderForImage(uploadData.path, bucket)
            .catch(err => console.warn('Error creating placeholder in background:', err));
        }
      } catch (err) {
        // Ignore errors in background task
      }
    }, 10);

    // Get the public URL of the original image
    const { data } = supabase.storage.from(bucket).getPublicUrl(uploadData.path);
    const publicUrl = data?.publicUrl;

    // Check if there's already an index image for this service
    if (isIndexImage) {
      const { data: existingIndexImages } = await supabase
        .from('service_images')
        .select('id')
        .eq('service_id', serviceId)
        .eq('is_index_image', true);
      
      // If we found existing index images, update them to not be index images
      if (existingIndexImages && existingIndexImages.length > 0) {
        console.log('Resetting existing index images:', existingIndexImages.length);
        await supabase
          .from('service_images')
          .update({ is_index_image: false })
          .eq('service_id', serviceId)
          .eq('is_index_image', true);
      }
    }
    
    // Create entry in service_images table linking the service to the image
    const { data: linkData, error: linkError } = await supabase
      .from('service_images')
      .insert({
        service_id: serviceId,
        is_index_image: isIndexImage,
        is_details_image: true,
        display_order: 0,
        bucket: bucket,
        original_path: uploadData.path,
        checksum: checksum
      })
      .select()
      .single();
      
    if (linkError) {
      console.error('Error linking image to service:', linkError);
      throw linkError;
    }

    // For backward compatibility - still update the service with the image URL
    const { error: updateError } = await supabase
      .from('services')
      .update({ 
        image_url: publicUrl
      })
      .eq('id', serviceId);

    if (updateError) {
      console.error('Update error:', updateError);
      throw updateError;
    }
    
    // Invalidate any cached versions of this image
    if (navigator.serviceWorker?.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'INVALIDATE_IMAGE_CACHE',
        url: publicUrl
      });
    }

    // Generate transformation URLs with smart quality based on whether it's already cropped
    const baseQuality = isCropped ? 90 : 85; // Higher quality for pre-cropped images
    const variants = {
      main: getTransformationUrl(bucket, uploadData.path, 800, undefined, undefined, baseQuality),
      preview: getTransformationUrl(bucket, uploadData.path, 400, undefined, undefined, Math.max(baseQuality - 15, 70)),
      card: getTransformationUrl(bucket, uploadData.path, 400, 300, undefined, baseQuality - 10) // Specific card dimensions
    };

    return {
      id: linkData.id,
      url: publicUrl,
      bucket,
      path: uploadData.path,
      variants,
      isIndexImage,
      serviceImageId: linkData.id,
      isCropped
    };
  } catch (error) {
    console.error('Error in updateServiceImage:', error);
    throw error;
  }
};

// Function to get all images for a service
export const getServiceImages = async (serviceId: number) => {
  try {
    const { data, error } = await supabase
      .from('service_images')
      .select(`
        id,
        is_index_image,
        is_details_image,
        display_order,
        bucket,
        original_path,
        checksum
      `)
      .eq('service_id', serviceId)
      .order('display_order', { ascending: true });
      
    if (error) {
      console.error('Error fetching service images:', error);
      throw error;
    }
    
    // For each image, generate transformation URLs
    const processedData = data?.map(image => {
      // Only generate variants if we have bucket and path
      if (image.bucket && image.original_path) {
        // Generate transformation URLs for various sizes, letting Supabase handle format optimization
        const variants = {
          main: getTransformationUrl(image.bucket, image.original_path, 800, undefined, undefined, 85),
          preview: getTransformationUrl(image.bucket, image.original_path, 400, undefined, undefined, 75),
          thumbnail: getTransformationUrl(image.bucket, image.original_path, 200, undefined, undefined, 70)
        };
        
        // Get original URL
        const { data: urlData } = supabase.storage
          .from(image.bucket)
          .getPublicUrl(image.original_path);
          
        return {
          ...image,
          variants,
          originalUrl: urlData?.publicUrl
        };
      }
      
      return image;
    }) || [];
    
    return processedData;
  } catch (error) {
    console.error('Error in getServiceImages:', error);
    throw error;
  }
};

// Function to migrate existing services with image_url to the new model
export const migrateServiceImage = async (serviceId: number) => {
  try {
    // Get the existing service with image data
    const { data: service, error: serviceError } = await supabase
      .from('services')
      .select('id, image_url')
      .eq('id', serviceId)
      .single();
      
    if (serviceError || !service || !service.image_url) {
      console.error('Error fetching service or service has no image:', serviceError);
      return null;
    }
    
    // Extract path from the full URL (varies based on your URL structure)
    // This is an approximation, might need adjustment
    const urlParts = service.image_url.split('/');
    const originalPath = urlParts[urlParts.length - 1];
    const bucket = 'services'; // Assume services bucket
    
    // Generate a simple checksum for the file
    const checksum = `migration-${originalPath}`;
    
    // Create entry in service_images table
    const { data: linkData, error: linkError } = await supabase
      .from('service_images')
      .insert({
        service_id: serviceId,
        is_index_image: true,
        is_details_image: true,
        display_order: 0,
        bucket: bucket,
        original_path: originalPath,
        checksum: checksum
      })
      .select()
      .single();
      
    if (linkError) {
      console.error('Error creating service image record:', linkError);
      return null;
    }
    
    return {
      id: linkData.id,
      url: service.image_url,
      bucket,
      path: originalPath
    };
  } catch (error) {
    console.error('Error in migrateServiceImage:', error);
    return null;
  }
};

// Utility function to set an image as the index image for a service
export const setAsIndexImage = async (serviceId: number, imageId: string) => {
  try {
    // First, set all images for this service to NOT be index images
    await supabase
      .from('service_images')
      .update({ is_index_image: false })
      .eq('service_id', serviceId);
      
    // Then set the selected image as index image
    const { error } = await supabase
      .from('service_images')
      .update({ is_index_image: true })
      .eq('service_id', serviceId)
      .eq('id', imageId);
      
    if (error) {
      console.error('Error setting index image:', error);
      throw error;
    }
    
    return true;
  } catch (error) {
    console.error('Error in setAsIndexImage:', error);
    throw error;
  }
};

// Delete a service image
export const deleteServiceImage = async (serviceId: number, imageId: string) => {
  try {
    // Get the image details first
    const { data: imageData, error: fetchError } = await supabase
      .from('service_images')
      .select(`
        bucket,
        original_path
      `)
      .eq('service_id', serviceId)
      .eq('id', imageId)
      .single();
      
    if (fetchError || !imageData) {
      console.error('Error fetching image details:', fetchError);
      throw fetchError;
    }
    
    // Delete the image record
    const { error: deleteError } = await supabase
      .from('service_images')
      .delete()
      .eq('service_id', serviceId)
      .eq('id', imageId);
      
    if (deleteError) {
      console.error('Error deleting service image:', deleteError);
      throw deleteError;
    }
    
    // Delete the actual file from storage if possible
    if (imageData.bucket && imageData.original_path) {
      const { error: storageDeleteError } = await supabase.storage
        .from(imageData.bucket)
        .remove([imageData.original_path]);
        
      if (storageDeleteError) {
        console.error('Error deleting file from storage:', storageDeleteError);
        // Don't throw here, still consider the deletion successful
      }
    }
    
    return true;
  } catch (error) {
    console.error('Error in deleteServiceImage:', error);
    throw error;
  }
};
